import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../core/store/store';
import { setUser } from '../../core/store/slices/userSlice';
import ZoneLoadingScreen from './ZoneLoadingScreen';
import { getApiBaseUrl } from '../../core/config/api';

const zoneMap: Record<string, string> = {
  '/': 'Accueil',
  '/dashboard': 'Tableau de bord',
  '/game/map': 'Carte du jeu',
  '/panel': 'Panel d\'administration',
  '/panel/users/roles': 'Gestion des rôles',
  '/panel/roles/default-access': 'Accès par défaut',
  '/panel/users': 'Gestion des utilisateurs',
  '/panel/support/team': 'Support équipe',
};

const NavigationLoader: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [fromZone, setFromZone] = useState<string>('');
  const [toZone, setToZone] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Chargement...');
  const previousPathRef = React.useRef<string>(location.pathname);
  const isInitialMount = React.useRef<boolean>(true);
  const isLoadingRef = React.useRef<boolean>(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Vérifier si on doit afficher le loader
  const shouldShowLoader = (from: string, to: string): boolean => {
    // Afficher le loader lors de la connexion depuis l'accueil vers le dashboard
    if (from === '/' && to === '/dashboard') {
      return true;
    }
    // Afficher le loader lors de la navigation du dashboard vers la map
    if (from === '/dashboard' && to === '/game/map') {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    
    // Ignorer le premier rendu
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousPathRef.current = currentPath;
      return;
    }
    
    // Si c'est un changement de route
    if (previousPath !== currentPath) {
      // Vérifier si on doit afficher le loader
      if (shouldShowLoader(previousPath, currentPath)) {
        // Réinitialiser le flag de chargement si on change de route
        if (isLoadingRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
        const fromZoneName = zoneMap[previousPath] || '';
        const toZoneName = zoneMap[currentPath] || '';
        
        setFromZone(fromZoneName);
        setToZone(toZoneName);
        setIsLoading(true);
        isLoadingRef.current = true;
        setProgress(0);
        setLoadingText('Chargement...');

        // Charger réellement les données nécessaires
        let isMounted = true;
        let finishTimer: NodeJS.Timeout | null = null;
        
        const loadData = async () => {
          const token = localStorage.getItem('token');
          
          if (!token) {
            // Pas de token, cacher le loader
            if (isMounted) {
              setIsLoading(false);
              isLoadingRef.current = false;
              previousPathRef.current = currentPath;
            }
            return;
          }

          try {
            // Pour la navigation vers /dashboard, charger le profil utilisateur
            if (currentPath === '/dashboard') {
              if (isMounted) {
                setLoadingText('Chargement du profil...');
                setProgress(30);
              }
              
              const profileResponse = await fetch(`${getApiBaseUrl(true)}/auth/profile`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                if (profileData.success && profileData.user) {
                  // Mettre à jour l'utilisateur dans Redux
                  const userForRedux = {
                    ...profileData.user,
                    activated: profileData.user.activated !== undefined ? profileData.user.activated : true,
                    bann: 0,
                    premium: false,
                  };
                  dispatch(setUser(userForRedux as any));
                  if (isMounted) {
                    setProgress(70);
                    setLoadingText('Finalisation...');
                  }
                }
              }
            }
            
            // Pour la navigation vers /game/map, charger les bâtiments
            if (currentPath === '/game/map') {
              if (isMounted) {
                setLoadingText('Chargement des bâtiments...');
                setProgress(30);
              }
              
              const buildingsResponse = await fetch(`${getApiBaseUrl(true)}/buildings`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });

              if (buildingsResponse.ok) {
                if (isMounted) {
                  setProgress(70);
                  setLoadingText('Finalisation...');
                }
              }
            }

            // Finir la progression
            if (isMounted) {
              setProgress(100);
              setLoadingText('Finalisation...');
            }
            
            // Attendre un peu pour que la page se charge complètement
            // et s'assurer que le Dashboard peut s'afficher
            finishTimer = setTimeout(() => {
              // Ne pas vérifier isMounted ici, forcer le cache du loader
              setIsLoading(false);
              isLoadingRef.current = false;
              setProgress(0);
              setLoadingText('Chargement...');
              previousPathRef.current = currentPath;
            }, 500); // Délai pour permettre au Dashboard de se charger et s'afficher
            
            // Stocker le timer dans la ref pour pouvoir le nettoyer
            timeoutRef.current = finishTimer;
          } catch (error) {
            console.error('Erreur lors du chargement des données:', error);
            // En cas d'erreur, cacher quand même le loader après un délai
            finishTimer = setTimeout(() => {
              if (isMounted) {
                setIsLoading(false);
                isLoadingRef.current = false;
                setProgress(0);
                setLoadingText('Chargement...');
                previousPathRef.current = currentPath;
              }
            }, 500);
          }
        };

        loadData();

        previousPathRef.current = currentPath;

        return () => {
          isMounted = false;
          if (finishTimer) {
            clearTimeout(finishTimer);
          }
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        };
      } else {
        // Si on ne doit pas afficher le loader, mettre à jour la référence immédiatement
        previousPathRef.current = currentPath;
      }
    } else {
      // Même chemin, mettre à jour la référence
      previousPathRef.current = currentPath;
    }
  }, [location.pathname, currentUser, dispatch]);

  // Mécanisme de sécurité : forcer le cache du loader si on reste bloqué trop longtemps
  useEffect(() => {
    if (isLoading && progress >= 100) {
      const safetyTimer = setTimeout(() => {
        setIsLoading(false);
        isLoadingRef.current = false;
        setProgress(0);
        setLoadingText('Chargement...');
      }, 1000); // Après 1 seconde à 100%, forcer le cache

      return () => clearTimeout(safetyTimer);
    }
  }, [isLoading, progress]);

  // Nettoyer les timers au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Ne pas afficher le loader si on n'est pas en train de charger
  if (!isLoading) {
    return null;
  }

  return (
    <ZoneLoadingScreen
      fromZone={fromZone}
      toZone={toZone}
      progress={progress}
      loadingText={loadingText}
    />
  );
};

export default NavigationLoader;

